import React, { useEffect, useState } from "react";
import { CenteredContainer } from "../../../components/CenteredContainer/CenteredContainer";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { styles } from "./StatsView.styles";
import { Backdrop, CircularProgress, Typography, WithStyles } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { IStatsFilterOptionType, IStatsFilters, StatsFilter } from "./StatsFilter/StatsFilter";
import { IStatsTab, StatsTabs } from "./StatsTabs";
import { SalesView } from "./views/SalesView";
import { SalesByUserView } from "./views/SalesByUserView";
import { SalesByProductView } from "./views/SalesByProductView";
import { NewUsersView } from "./views/NewUsersView";
import { SubscribersView } from "./views/SubscribersView";
import { convertDateStringToNumber, today } from "../../../../utilities/dateTimeUtils";
import { DATE_FORMAT } from "../../../../constants/TIME";
import { OneOffsView } from "./views/OneOffsView";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import * as StatisticsAction from "../../../../stores/statistics/StatisticsAction";
import { useSelector, useDispatch } from "react-redux";
import { orderStateMapper, paymentMethodMapper } from "../../../../constants/ORDER_DETAILS";
import moment from "moment";
import { ReportsView } from "./views/ReportsView";

const statsTabs: IStatsTab[] = [
    {
        title: "Sales",
        filters: ["orderType", "orderState", "paymentMethod"],
        view: <SalesView />
    },
    {
        title: "Sales By User",
        filters: ["orderType", "orderState", "paymentMethod"],
        view: <SalesByUserView />,
        action: StatisticsAction.requestSalesByUser,
        actionId: StatisticsAction.REQUEST_SALES_BY_USER
    },
    {
        title: "Sales By Product",
        filters: ["orderType", "orderState", "paymentMethod"],
        view: <SalesByProductView />,
        action: StatisticsAction.requestSalesByProduct,
        actionId: StatisticsAction.REQUEST_SALES_BY_PRODUCT
    },
    {
        title: "New Users",
        filters: [],
        view: <NewUsersView />
    },
    {
        title: "Subscribers",
        filters: ["users"],
        view: <SubscribersView />,
        action: StatisticsAction.requestSubscribers,
        actionId: StatisticsAction.REQUEST_SUBSCRIBERS
    },
    {
        title: "One-offs",
        filters: ["orderType", "orderState", "paymentMethod"],
        view: <OneOffsView />,
        action: StatisticsAction.requestOneOffs,
        actionId: StatisticsAction.REQUEST_ONE_OFFS
    },
    {
        title: "Reports",
        filters: ["users"],
        view: <ReportsView />,
        action: StatisticsAction.requestSingleIsinReports,
        actionId: StatisticsAction.REQUEST_REPORTS
    },
];

export interface IStatsViewProps extends WithStyles<typeof styles> {}

const StatsViewPure: React.FunctionComponent<IStatsViewProps> = ({ classes }: IStatsViewProps) => {
    const dispatch = useDispatch();
    const [from, setFrom] = useState<number>(convertDateStringToNumber(today().subtract(1, "M").format(DATE_FORMAT)));
    const [to, setTo] = useState<number>(convertDateStringToNumber(today().format(DATE_FORMAT)));
    const [filters, setFilters] = useState<IStatsFilters>({} as IStatsFilters);
    const [selectedTab, setSelectedTab] = useState<number>(4);
    const [isUpdateButtonEnabled, setIsUpdateButtonEnabled] = useState(false);
    const isLoading = useSelector(state => selectRequesting(state, statsTabs.map(tab => tab.actionId)));

    const updateData = () => {
        const currecntTabDef = statsTabs[selectedTab];
        const params = {
            from,
            to,
            ...(currecntTabDef?.filters.includes("orderType") && {
                orderTypeFilter: filters["orderType"]?.toString().toUpperCase()
            }),
            ...(currecntTabDef?.filters.includes("orderState") && {
                orderStateFilter: filters["orderState"]?.map(filter => orderStateMapper[filter]).join()
            }),
            ...(currecntTabDef?.filters.includes("paymentMethod") && {
                paymentMethodFilter: filters["paymentMethod"]?.map(filter => paymentMethodMapper[filter]).join()
            }),
            ...(currecntTabDef?.filters.includes("users") && {
                internal: filters["users"]?.includes("Internal"),
                external: filters["users"]?.includes("External")
            })
        };
        const fromDate = moment(from, DATE_FORMAT);
        const toDate = moment(from, DATE_FORMAT);
        if (fromDate.isValid() && toDate.isValid()) {
            const curentAction = statsTabs[selectedTab]?.action;
            if (curentAction) dispatch(curentAction(params));
        }
        setIsUpdateButtonEnabled(false);
    };

    useEffect(() => {
        updateData();
        // eslint-disable-next-line
    }, [selectedTab]);

    const handleFilterChange = (filter: IStatsFilterOptionType, values: string[]) => {
        const newFilters = {
            ...filters,
            [filter]: values
        };
        setFilters(newFilters);
        setIsUpdateButtonEnabled(true);
    };

    const handleFromChange = (newFrom: number) => {
        setFrom(newFrom);
        setIsUpdateButtonEnabled(true);
    };

    const handleToChange = (newTo: number) => {
        setTo(newTo);
        setIsUpdateButtonEnabled(true);
    };

    return (
        <CenteredContainer>
            <Backdrop open={isLoading} className={classes.backDrop}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Breadcrumbs items={[{ title: "Administration panel", href: "/admin" }, { title: "Stats" }]} />
            <div className={classes.title}>
                <Typography variant="h3">Statistics</Typography>
            </div>
            <StatsFilter
                shownFilters={statsTabs[selectedTab]?.filters}
                from={from}
                to={to}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                filters={filters}
                onFiltersChange={handleFilterChange}
                onUpdate={() => updateData()}
                isUpdateButtonEnabled={isUpdateButtonEnabled}
            />
            <StatsTabs
                tabs={statsTabs}
                from={from}
                to={to}
                filters={filters}
                selectedTab={selectedTab}
                onTabChange={tabIndex => setSelectedTab(tabIndex)}
            />
        </CenteredContainer>
    );
};

export const StatsView = withStyles(styles)(StatsViewPure);
